import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { deleteUser, getAllUsers } from "@/api/users"; // Import your API function
import { RoleType, User } from "@/models/user.model";
import { toast } from "@/hooks/use-toast";

interface UsersState {
  users: User[];
  searchResults: User[];
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  users: [],
  searchResults: [],
  loading: false,
  error: null as string | null,
};

// Thunk to fetch all users
export const fetchAllUsers = createAsyncThunk(
  "users/fetchAllUsers",
  async () => {
    const data = await getAllUsers({});
    return data;
  }
);

// Async thunk to fetch search results
export const fetchSearchResults = createAsyncThunk(
  "users/fetchSearchResults",
  async (args: {
    search?: string;
    page?: number;
    limit?: number;
    role?: RoleType;
  }) => {
    const data = await getAllUsers(args);
    return data;
  }
);

export const removeUser = createAsyncThunk(
  "users/removeUser",
  async (userId: number, { dispatch, rejectWithValue }) => {
    const res = await deleteUser(userId);
    if (res) {
      await dispatch(fetchSearchResults({ search: "", role: RoleType.STAFF }));
      return userId;
    } else {
      return rejectWithValue("Failed to delete the user");
    }
  }
);

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setUsers: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchAllUsers.fulfilled,
        (state, action: PayloadAction<User[]>) => {
          state.users = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchAllUsers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchSearchResults.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchSearchResults.fulfilled,
        (state, action: PayloadAction<User[]>) => {
          state.searchResults = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchSearchResults.rejected, (state) => {
        state.loading = false;
      })
      .addCase(removeUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeUser.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted user from the users list
        state.users = state.users.filter((user) => user.id !== action.payload);
      })
      .addCase(removeUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUsers } = usersSlice.actions;
export default usersSlice.reducer;
